import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useMother } from '../lib/MotherContext';
import { createMilkLog, listMilkLogs, deleteMilkLog } from '../lib/api';
import { Plus, Trash2, Droplets, X, Info, UserPlus, LogIn, Home } from 'lucide-react';
import { toast } from 'sonner';

const todayKey = () => new Date().toISOString().slice(0, 10);

export default function MilkLog() {
  const { lang, t } = useLanguage();
  const { mother, loading: motherLoading } = useMother();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date_time: new Date().toISOString().slice(0, 16),
    method: 'pump',
    left_ml: 0,
    right_ml: 0,
    duration: 15,
    pain_score: 0,
    has_lump: false,
    has_fever: false,
    wet_diapers: 0,
    notes: '',
  });

  const refresh = () => {
    if (!mother) {
      setLogs([]);
      setLoading(false);
      return;
    }
    listMilkLogs()
      .then(setLogs)
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [mother]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await createMilkLog({
        ...form,
        left_ml: Number(form.left_ml),
        right_ml: Number(form.right_ml),
        duration: Number(form.duration),
        pain_score: Number(form.pain_score),
        wet_diapers: Number(form.wet_diapers) || 0,
      });
      toast.success(lang === 'zh' ? '已保存' : 'Saved');
      setShowForm(false);
      setForm({ ...form, left_ml: 0, right_ml: 0, notes: '' });
      refresh();
    } catch (err) {
      toast.error(lang === 'zh' ? '保存失败' : 'Save failed');
    }
  };

  const remove = async (id) => {
    try {
      await deleteMilkLog(id);
      toast.success(lang === 'zh' ? '已删除' : 'Deleted');
      refresh();
    } catch {
      toast.error('Delete failed');
    }
  };

  const todayLogs = logs.filter((l) => l.date_time && l.date_time.slice(0, 10) === todayKey());
  const totalToday = todayLogs.reduce((sum, l) => sum + (l.left_ml || 0) + (l.right_ml || 0), 0);
  const leftToday = todayLogs.reduce((sum, l) => sum + (l.left_ml || 0), 0);
  const rightToday = todayLogs.reduce((sum, l) => sum + (l.right_ml || 0), 0);

  // Guest gate: milk logging requires a profile so records persist for this mother only.
  if (!motherLoading && !mother) {
    return (
      <div className="container-app py-10 sm:py-16 max-w-lg animate-fade-in-up" data-testid="milklog-guest-gate">
        <div className="card-base p-6 sm:p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-terracotta-light flex items-center justify-center mb-4 mx-auto">
            <Droplets size={22} className="text-terracotta" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-3">
            {t('milklog_title')}
          </h1>
          <p className="text-sm text-ink-secondary leading-relaxed mb-6">
            {lang === 'zh'
              ? '奶量记录需要创建个人档案后才能保存，这样你下次回来才看得到自己的记录。'
              : 'Milk logging requires a personal profile so you can save and view your own records next time.'}
          </p>
          <div className="space-y-2">
            <Link
              to="/profile/start?next=/milk-log"
              data-testid="milklog-gate-create-btn"
              className="btn-primary w-full"
            >
              <UserPlus size={14} className="mr-1" />
              {lang === 'zh' ? '创建个人档案' : 'Create Profile'}
            </Link>
            <Link
              to="/profile/login?next=/milk-log"
              data-testid="milklog-gate-login-btn"
              className="btn-outline w-full"
            >
              <LogIn size={14} className="mr-1" />
              {lang === 'zh' ? '已有档案，登入' : 'Log In'}
            </Link>
            <Link
              to="/"
              data-testid="milklog-gate-home-btn"
              className="block text-center text-sm text-ink-tertiary hover:text-terracotta py-2"
            >
              <Home size={12} className="inline mr-1" />
              {lang === 'zh' ? '返回首页' : 'Back Home'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-app py-8 sm:py-12 animate-fade-in-up" data-testid="milk-log-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink">{t('milklog_title')}</h1>
        <button
          onClick={() => setShowForm(true)}
          data-testid="add-log-btn"
          className="btn-primary"
        >
          <Plus size={18} className="mr-1" /> {t('milklog_add')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="card-base p-5" data-testid="stat-total-today">
          <p className="text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('milklog_today_total')}</p>
          <p className="font-heading text-3xl font-semibold text-terracotta">{totalToday} <span className="text-base font-normal text-ink-tertiary">ml</span></p>
        </div>
        <div className="card-base p-5">
          <p className="text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('milklog_sessions')}</p>
          <p className="font-heading text-3xl font-semibold text-ink">{todayLogs.length}</p>
        </div>
        <div className="card-base p-5">
          <p className="text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('milklog_left_vs_right')}</p>
          <p className="font-heading text-xl font-semibold text-ink">
            <span className="text-sage">{leftToday}</span>
            <span className="text-ink-tertiary mx-2 text-base">·</span>
            <span className="text-terracotta">{rightToday}</span>
            <span className="text-sm text-ink-tertiary ml-1">ml</span>
          </p>
        </div>
      </div>

      {/* Reminder */}
      <div className="card-base p-4 mb-8 flex gap-3 items-start bg-sage-light/30 border-sage/30">
        <Info size={16} className="text-sage flex-shrink-0 mt-0.5" />
        <p className="text-sm text-ink-secondary">{t('milklog_reminder')}</p>
      </div>

      {/* Log list */}
      <h2 className="font-heading text-xl font-semibold text-ink mb-3">
        {lang === 'zh' ? '所有记录' : 'All Records'}
      </h2>
      {loading ? (
        <p className="text-ink-tertiary py-10 text-center">{t('loading')}</p>
      ) : logs.length === 0 ? (
        <div className="card-base p-12 text-center" data-testid="no-logs">
          <Droplets size={32} className="text-ink-tertiary mx-auto mb-3" />
          <p className="text-ink-tertiary">{t('milklog_no_records')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((l) => (
            <div key={l.id} className="card-base p-5 flex items-center justify-between gap-4" data-testid={`log-${l.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-ink-tertiary mb-1">
                  {new Date(l.date_time).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-MY')}
                </p>
                <p className="font-medium text-ink">
                  {l.method === 'latch' ? t('milklog_method_latch') : l.method === 'pump' ? t('milklog_method_pump') : t('milklog_method_hand')}
                  <span className="text-ink-tertiary ml-2">·</span>
                  <span className="ml-2 text-sage font-semibold">{l.left_ml}</span>
                  <span className="text-ink-tertiary mx-1">/</span>
                  <span className="text-terracotta font-semibold">{l.right_ml}</span>
                  <span className="ml-1 text-sm text-ink-tertiary">ml</span>
                </p>
                {(l.pain_score > 0 || l.has_lump || l.has_fever) && (
                  <p className="text-xs text-ink-tertiary mt-1">
                    {l.pain_score > 0 && `${t('milklog_pain')}: ${l.pain_score} `}
                    {l.has_lump && '· ' + (lang === 'zh' ? '硬块' : 'lump') + ' '}
                    {l.has_fever && '· ' + (lang === 'zh' ? '发烧' : 'fever')}
                  </p>
                )}
                {l.notes && <p className="text-xs text-ink-secondary mt-1 italic">{l.notes}</p>}
              </div>
              <button
                onClick={() => remove(l.id)}
                data-testid={`del-${l.id}`}
                className="text-ink-tertiary hover:text-risk-red p-2"
                aria-label="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            data-testid="log-form"
            className="bg-paper rounded-3xl p-6 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-semibold text-ink">{t('milklog_add')}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 rounded-full hover:bg-subtle">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-2">Date / Time</label>
                <input
                  type="datetime-local"
                  value={form.date_time}
                  onChange={(e) => setForm({ ...form, date_time: e.target.value })}
                  className="input-base"
                  required
                  data-testid="form-date"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-2">{t('milklog_method')}</label>
                <div className="grid grid-cols-3 gap-2">
                  {['latch', 'pump', 'hand'].map((m) => (
                    <button
                      type="button"
                      key={m}
                      onClick={() => setForm({ ...form, method: m })}
                      data-testid={`method-${m}`}
                      className={`min-h-[48px] rounded-xl border-2 text-sm font-medium ${
                        form.method === m ? 'border-terracotta bg-terracotta-light/50' : 'border-border'
                      }`}
                    >
                      {t(`milklog_method_${m}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-ink-secondary mb-2">{t('milklog_left_ml')}</label>
                  <input type="number" min="0" value={form.left_ml} onChange={(e) => setForm({ ...form, left_ml: e.target.value })} className="input-base" data-testid="form-left" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-secondary mb-2">{t('milklog_right_ml')}</label>
                  <input type="number" min="0" value={form.right_ml} onChange={(e) => setForm({ ...form, right_ml: e.target.value })} className="input-base" data-testid="form-right" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-ink-secondary mb-2">{t('milklog_duration')}</label>
                  <input type="number" min="0" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="input-base" data-testid="form-duration" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-secondary mb-2">{t('milklog_pain')}</label>
                  <input type="number" min="0" max="10" value={form.pain_score} onChange={(e) => setForm({ ...form, pain_score: e.target.value })} className="input-base" data-testid="form-pain" />
                </div>
              </div>

              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.has_lump} onChange={(e) => setForm({ ...form, has_lump: e.target.checked })} className="w-5 h-5" data-testid="form-lump" />
                  <span className="text-sm">{t('milklog_lump')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.has_fever} onChange={(e) => setForm({ ...form, has_fever: e.target.checked })} className="w-5 h-5" data-testid="form-fever" />
                  <span className="text-sm">{t('milklog_fever')}</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-2">{t('milklog_diapers')}</label>
                <input type="number" min="0" value={form.wet_diapers} onChange={(e) => setForm({ ...form, wet_diapers: e.target.value })} className="input-base" data-testid="form-diapers" />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-2">{t('milklog_notes')}</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-base min-h-[80px]" data-testid="form-notes" />
              </div>

              <button type="submit" data-testid="form-submit" className="btn-primary w-full">
                {t('save')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
